// ContactSheet — a modal overlay that turns a Clip into a contact-Sheet PNG.
//
// A thin React frame over the framework-agnostic cores: it fetches the Clip from
// its presigned R2 URL, decodes the whole clip, and renders the Sheet PNG (the
// whole-clip even sampling lives in src/cores/sheet.ts). The owner can toggle the
// two layouts (5×5 square / 4×8 strip) and the two looks (seamless / hairline),
// preview the result, and download it — mirroring the device-served web/sheet.js
// overlay, but in the secure cloud context.

import { useCallback, useEffect, useRef, useState } from "react";
import type { MediaItem } from "../api";
import { filenameFor, uniqueDownloadName } from "../download";
import {
  defaultSheetCores,
  sheetFilename,
  type GifFrame,
  type RenderSheetOptions,
  type SheetCores,
  type SheetLayoutName,
} from "../cores";
import { Modal } from "./Modal";

type Look = NonNullable<RenderSheetOptions["look"]>;

export interface ContactSheetProps {
  /** The Clip to render (its `url` is the presigned R2 GET). */
  item: MediaItem;
  /** Close the overlay. */
  onClose: () => void;
  /**
   * Injectable decode/render seam (tests stub it); defaults to the real cores. The clip
   * is DECODED once (decodeClip) and the sheet RE-RENDERED from those frames on each
   * toggle (renderSheet) — so a layout/look change never re-hits the short-TTL URL (#138).
   */
  sheetCores?: SheetCores;
}

export function ContactSheet({
  item,
  onClose,
  sheetCores = defaultSheetCores,
}: ContactSheetProps): React.JSX.Element {
  const [layout, setLayout] = useState<SheetLayoutName>("square");
  const [look, setLook] = useState<Look>("seamless");
  const [frames, setFrames] = useState<readonly GifFrame[] | null>(null);
  const [pngUrl, setPngUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  // Bumped by Retry to re-decode (a transient network blip can clear).
  const [retryNonce, setRetryNonce] = useState(0);

  const { decodeClip, renderSheet } = sheetCores;

  // The current object URL, revoked when replaced/unmounted (no leaks).
  const urlRef = useRef<string | null>(null);
  const setObjectUrl = useCallback((url: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setPngUrl(url);
  }, []);

  // 1) Decode ONCE per clip (fetch + decode) — on open / url change / Retry. This is the
  //    only step that hits the short-TTL URL, so a toggle below never re-fetches it.
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    setFrames(null);
    decodeClip(item.url)
      .then((f) => {
        if (!cancelled) setFrames(f);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "";
        // Only the explicit "could not read this clip" (zero decoded frames) means a bad /
        // too-short clip. EVERY other failure — an expired short-TTL URL on a left-open overlay
        // (the usual culprit), a network `TypeError: Failed to fetch`, a non-OK status — is a
        // transient fetch issue, so default to the retryable message (#138).
        if (/could not read this clip/i.test(msg)) {
          setError("Couldn’t read this clip — it may be too short or not a valid Clip.");
        } else {
          setError(
            "This preview link expired — they’re short-lived. Retry, or reload the gallery.",
          );
        }
        setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.url, decodeClip, retryNonce]);

  // 2) Re-render the PNG from the CACHED frames on every layout/look toggle — cheap, no fetch.
  useEffect(() => {
    if (!frames) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    renderSheet(frames, { layout, look })
      .then((blob) => {
        if (cancelled) return;
        setObjectUrl(URL.createObjectURL(blob));
        setBusy(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn’t render the contact sheet.");
        setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [frames, layout, look, renderSheet, setObjectUrl]);

  // Revoke the last object URL on unmount.
  useEffect(() => () => setObjectUrl(null), [setObjectUrl]);

  const downloadName = sheetFilename(filenameFor(item), layout, look);

  return (
    <Modal label={`Contact sheet for ${filenameFor(item)}`} onClose={onClose} testId="contact-sheet">
        {error ? (
          <div className="sheet-overlay__msg sheet-overlay__msg--error" role="alert">
            <p>{error}</p>
            <button
              type="button"
              className="btn"
              onClick={() => setRetryNonce((n) => n + 1)}
              data-testid="sheet-retry"
            >
              Retry
            </button>
          </div>
        ) : busy && !pngUrl ? (
          <p className="sheet-overlay__msg" role="status">
            Generating…
          </p>
        ) : (
          pngUrl && (
            <img
              className="sheet-overlay__img"
              src={pngUrl}
              alt={`Contact sheet for ${filenameFor(item)}`}
              data-testid="sheet-preview"
            />
          )
        )}

        <div className="sheet-overlay__ctl">
          <ToggleGroup
            label="Layout"
            value={layout}
            options={[
              { value: "square", label: "5×5" },
              { value: "strip", label: "4×8" },
            ]}
            onChange={setLayout}
            disabled={busy}
          />
          <ToggleGroup
            label="Look"
            value={look}
            options={[
              { value: "seamless", label: "seamless" },
              { value: "hairline", label: "hairline" },
            ]}
            onChange={setLook}
            disabled={busy}
          />
        </div>

        <a
          className="sheet-overlay__dl"
          href={pngUrl ?? undefined}
          download={uniqueDownloadName(downloadName)}
          aria-disabled={!pngUrl}
          data-testid="sheet-download"
        >
          Download PNG
        </a>
    </Modal>
  );
}

interface ToggleGroupProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

function ToggleGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: ToggleGroupProps<T>): React.JSX.Element {
  return (
    <div className="sheet-overlay__group" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={
            "sheet-overlay__opt" + (o.value === value ? " is-on" : "")
          }
          aria-pressed={o.value === value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
